import { z } from 'zod';

export const APP_NAME = 'VentureRoom';
export const skills = [
  'Frontend Engineering',
  'Backend Engineering',
  'Full Stack Engineering',
  'Mobile Engineering',
  'AI & Machine Learning',
  'Data Science & Analytics',
  'DevOps & Infrastructure',
  'Product Management',
  'UI/UX Design',
  'Graphic Design & Branding',
  'Performance Marketing',
  'Content Marketing & SEO',
  'Social Media Management',
  'Public Relations (PR)',
  'B2B Sales',
  'B2C Sales',
  'Business Strategy',
  'Operations Management',
  'Finance & Accounting',
  'Legal & Compliance',
  'Fundraising & VC',
  'Human Resources & Recruiting',
  'Customer Success & Support',
  'Project Management',
  'Copywriting',
  'Video Production',
  'Hardware Engineering',
  'Community Building',
  'Business Development',
] as const;
export const industries = [
  'SaaS & B2B Software',
  'E-commerce & Retail',
  'Fintech & Web3',
  'HealthTech & Biotech',
  'ClimateTech & Sustainability',
  'EdTech & Education',
  'Real Estate & PropTech',
  'Automotive & Mobility',
  'Manufacturing & Industrial',
  'Logistics & Supply Chain',
  'Food, Beverage & AgTech',
  'Hospitality & Travel',
  'Gaming & Esports',
  'Media & Entertainment',
  'Hardware & IoT',
  'Fashion & Apparel',
  'Consumer Goods (CPG)',
  'Agency & Professional Services',
  'Non-Profit & Social Impact',
  'Aerospace & Defense',
  'Telecommunications',
  'Cybersecurity',
  'LegalTech',
  'HRTech & Future of Work',
  'Marketplaces',
  'Creator Economy',
  'Fitness & Wellness',
] as const;
export const resourceOffers = [
  'Sweat Equity (Time & Effort)',
  'Initial Capital / Funding',
  'Domain Expertise',
  'Industry Connections / Network',
  'Technical Architecture',
  'Go-to-market Strategy',
  'Advisory / Mentorship',
  'Office Space / Resources',
  'Customer Base / Audience',
] as const;
export const resourceNeeds = [
  'Technical Co-founder',
  'Business / Sales Co-founder',
  'Marketing Co-founder',
  'Pre-seed Funding',
  'Seed Funding',
  'Advisors & Mentors',
  'Early Adopters / Beta Testers',
  'Design & UX Partner',
  'Legal & Compliance Expert',
] as const;
export const stages = [
  'Exploring',
  'Forming Team',
  'Validating',
  'Building MVP',
  'Operating',
  'Paused',
  'Closed',
] as const;
export const statuses = ['In Progress', 'Completed', 'Cancelled'] as const;
export const contributionTypes = [
  'Work delivered',
  'Time',
  'Expense paid',
  'Asset/resource supplied',
  'Introduction/contact',
  'Customer interview',
  'Sales activity',
  'Research',
  'Other',
] as const;
const id = z.string().min(1).max(100);
const short = z.string().trim().min(1).max(180);
const note = z.string().trim().max(3000);
const tags = z.preprocess(
  (val) => {
    if (typeof val === 'string') return val ? [val.substring(0, 60)] : [];
    if (!val) return [];
    return val;
  },
  z.array(z.string().min(1).max(60)).max(20).default([])
);
const date = z.iso.datetime();
const day = z.iso.date();
export const profileSchema = z.object({
  id,
  name: short,
  bio: note,
  location: short,
  avatar: z.enum(['coral', 'sage', 'lavender', 'sand', 'sky']),
  preference: z.enum(['Remote', 'Local', 'Hybrid']),
  role: z.enum(['Skill Provider', 'Business Owner', 'Both']).optional(),
  industries: tags,
  skills: tags,
  seeking: tags,
  background: note,
  brings: tags,
  wants: tags,
  hours: z.number().min(1).max(80),
  stage: z.enum(stages),
  style: z.enum(['Async first', 'Regular check-ins', 'Co-working']),
  commitment: z.enum(['Exploring', 'Side project', 'Full time']),
  idea: z.enum(['Have an idea', 'Join an idea', 'Either']),
  teamSize: z.number().int().min(2).max(12),
  looking: z.boolean(),
  subscriptionTier: z.enum(['free', 'starter', 'pro', 'enterprise', 'elite', 'agency']).optional(),
  team: z.array(id).max(20).optional(),
  stats: z
    .object({
      profileViews: z.number().int().min(0),
      searchAppearances: z.number().int().min(0),
    })
    .optional(),
});
export const ventureSchema = z.object({
  id,
  name: short,
  pitch: short,
  problem: note,
  solution: note,
  description: note,
  industry: short,
  stage: z.enum(stages),
  members: z.array(id).min(1).max(20),
  seeking: tags,
  hours: z.number().min(1).max(80),
  preference: z.enum(['Remote', 'Local', 'Hybrid']),
  location: short,
  milestone: short,
  cadence: short,
  createdAt: date,
  credits: z.object({
    balance: z.number(),
    purchaserId: z.string(),
    isShared: z.boolean()
  }).optional(),
  websiteStatsConfig: z.object({
    endpoint: z.string(),
    apiKey: z.string()
  }).optional(),
});
export const connectionSchema = z.object({
  id,
  from: id,
  to: id,
  status: z.enum(['pending', 'accepted', 'declined']),
  intro: note,
  createdAt: date,
});
export const bookmarkSchema = z.object({
  id,
  userId: id,
  personId: id,
  state: z.enum(['saved', 'passed']),
});
export const messageSchema = z.object({
  id,
  channel: id,
  author: id,
  text: z.string().trim().min(1).max(2000),
  imageUrl: z.string().optional(),
  fileUrl: z.string().optional(),
  fileName: z.string().optional(),
  websiteCode: z.object({ html: z.string(), css: z.string(), js: z.string() }).optional(),
  replyTo: id.optional(),
  reference: z.string().max(180).optional(),
  reactions: z.array(id),
  createdAt: date,
});
export const decisionSchema = z.object({
  id,
  ventureId: id,
  author: id,
  title: short,
  context: note,
  options: z
    .array(short)
    .min(2)
    .max(6)
    .refine(
      (v) => new Set(v.map((x) => x.toLowerCase())).size === v.length,
      'Options must be distinct',
    ),
  deadline: date,
  participants: z.array(id).min(1),
  votes: z.record(id, z.number().int().min(0)),
  comments: z.array(z.object({ author: id, text: short })),
  closedAt: date.optional(),
  createdAt: date,
});
export const commitmentSchema = z.object({
  id,
  ventureId: id,
  title: short,
  description: note,
  owner: id,
  dueDate: day,
  priority: z.enum(['Low', 'Medium', 'High']),
  status: z.preprocess((val) => {
    if (val === 'Planned' || val === 'Blocked') return 'In Progress';
    if (val === 'Canceled') return 'Cancelled';
    return val;
  }, z.enum(statuses)),
  effort: z.number().min(0).max(1000),
  milestone: short,
  comments: z.array(z.object({ author: id, text: short })),
  completedAt: date.optional(),
  createdAt: date,
});
export const eventSchema = z.object({
  id,
  ventureId: id,
  title: short,
  type: z.enum(['Meeting', 'Milestone', 'Deadline', 'Launch', 'Custom event']),
  startsAt: date,
  details: note,
  participants: z.array(id).min(1),
});
export const contributionSchema = z.object({
  id,
  ventureId: id,
  contributor: id,
  type: z.enum(contributionTypes),
  title: short,
  description: note,
  quantity: z.number().min(0).max(10000).optional(),
  expenseMinor: z.number().int().min(0).max(100000000).optional(),
  currency: z.literal('GBP'),
  date: day,
  commitmentId: id.optional(),
  evidence: note,
  createdAt: date,
});
export const activitySchema = z.object({
  id,
  ventureId: id.optional(),
  title: short,
  detail: note,
  kind: z.enum([
    'connection',
    'venture',
    'commitment',
    'decision',
    'contribution',
    'meeting',
    'message',
    'profile',
  ]),
  createdAt: date,
});
export const invitationSchema = z.object({
  id,
  ventureId: id,
  from: id,
  to: id,
  kind: z.enum(['invitation', 'request']),
  status: z.enum(['pending', 'accepted', 'declined']),
  createdAt: date,
});
export const endorsementSchema = z.object({
  id,
  from: id,
  to: id,
  skill: short,
  text: short,
  ventureId: id,
});
export const schemas = {
  profiles: profileSchema,
  ventures: ventureSchema,
  connections: connectionSchema,
  bookmarks: bookmarkSchema,
  messages: messageSchema,
  decisions: decisionSchema,
  commitments: commitmentSchema,
  events: eventSchema,
  contributions: contributionSchema,
  activities: activitySchema,
  invitations: invitationSchema,
  endorsements: endorsementSchema,
};
export type Profile = z.infer<typeof profileSchema>;
export type Venture = z.infer<typeof ventureSchema>;
export type Decision = z.infer<typeof decisionSchema>;
export type Commitment = z.infer<typeof commitmentSchema>;
export type Contribution = z.infer<typeof contributionSchema>;
export type EntityMap = { [K in keyof typeof schemas]: z.infer<(typeof schemas)[K]> };
export type Table = keyof EntityMap;
export type Snapshot = { [K in Table]: EntityMap[K][] };
export const tables = Object.keys(schemas) as Table[];
export function emptySnapshot(): Snapshot {
  return {
    profiles: [],
    ventures: [],
    connections: [],
    bookmarks: [],
    messages: [],
    decisions: [],
    commitments: [],
    events: [],
    contributions: [],
    activities: [],
    invitations: [],
    endorsements: [],
  };
}
export function parseSnapshot(value: unknown): Snapshot {
  return z
    .object(Object.fromEntries(tables.map((k) => [k, z.array(schemas[k])])))
    .parse(value) as Snapshot;
}
export const commandSchema = z.discriminatedUnion('type', [
  z.object({
    type: z.literal('topUpCredits'),
    ventureId: id,
    amount: z.number(),
    isShared: z.boolean(),
  }),
  z.object({
    type: z.literal('updateWebsiteStats'),
    ventureId: id,
    endpoint: z.string(),
    apiKey: z.string(),
  }),
  z.object({
      type: z.literal('subscribe'),
      tier: z.enum(['free', 'starter', 'pro', 'enterprise', 'elite', 'agency']),
    }),
  z.object({
    type: z.literal('teamMember'),
    action: z.enum(['add', 'remove']),
    personId: id,
  }),
  z.object({
    type: z.literal('track'),
    personId: id,
    kind: z.enum(['profileView', 'searchAppearance']),
  }),
    z.object({ type: z.literal('profile'), profile: profileSchema }),
  z.object({
    type: z.literal('bookmark'),
    personId: id,
    state: z.enum(['saved', 'passed', 'remove']),
  }),
  z.object({ type: z.literal('connect'), personId: id, intro: note }),
  z.object({ type: z.literal('connectionRespond'), id, accept: z.boolean() }),
  z.object({
    type: z.literal('message'),
    channel: id,
    text: z.string().trim().min(1).max(2000),
    imageUrl: z.string().optional(),
    fileUrl: z.string().optional(),
    fileName: z.string().optional(),
      websiteCode: z.object({ html: z.string(), css: z.string(), js: z.string() }).optional(),
      replyTo: id.optional(),
    reference: z.string().max(180).optional(),
  }),
  z.object({ type: z.literal('react'), id }),
  z.object({ type: z.literal('venture'), venture: ventureSchema }),
  z.object({ type: z.literal('stage'), ventureId: id, stage: z.enum(stages) }),
  z.object({ type: z.literal('invite'), ventureId: id, personId: id }),
  z.object({ type: z.literal('join'), ventureId: id }),
  z.object({ type: z.literal('invitationRespond'), id, accept: z.boolean() }),
  z.object({ type: z.literal('decision'), decision: decisionSchema }),
  z.object({ type: z.literal('vote'), id, option: z.number().int().min(0) }),
  z.object({ type: z.literal('closeDecision'), id }),
  z.object({ type: z.literal('decisionComment'), id, text: short }),
  z.object({ type: z.literal('commitment'), commitment: commitmentSchema }),
  z.object({ type: z.literal('commitmentStatus'), id, status: z.preprocess((val) => {
    if (val === 'Planned' || val === 'Blocked') return 'In Progress';
    if (val === 'Canceled') return 'Cancelled';
    return val;
  }, z.enum(statuses)) }),
  z.object({ type: z.literal('commitmentComment'), id, text: short }),
  z.object({ type: z.literal('event'), event: eventSchema }),
  z.object({ type: z.literal('contribution'), contribution: contributionSchema }),
  z.object({ type: z.literal('endorse'), endorsement: endorsementSchema }),
]);
export type Command = z.infer<typeof commandSchema>;
export function newId(prefix = 'item') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}
